import React, { useState } from 'react';
import { useServicesManager } from '@/hooks/useServicesManager';
import { useServiceFormState } from './services/ServiceFormState';
import { useServiceValidation } from './services/ServiceValidation';
import { ServicesHeader } from './services/ServicesHeader';
import { ServicesContent } from './services/ServicesContent';
import { NewServiceForm } from './services/NewServiceForm';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { X, Info } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Service {
  id: string;
  service_name: string;
  description: string | null;
  session_instructions: string | null;
  price: number;
  duration_minutes: number;
  formats_available: string[];
  is_active: boolean;
  currency: string;
  service_photo: string | null;
  is_free_session: boolean;
  parental_consent_required?: boolean;
  adults_only?: boolean;
  created_at: string;
  updated_at: string;
}

interface ServicesManagerProps {
  userId: string;
}

export const ServicesManager: React.FC<ServicesManagerProps> = ({ userId }) => {
  const { services, isLoading, isSubmitting, addService, updateService, toggleServiceActive, deleteService } = useServicesManager(userId);
  const {
    newService,
    selectedFormats,
    setNewService,
    setSelectedFormats,
    resetForm,
    handleFormatChange,
    handleInputChange,
    handleSelectChange,
    handleToggleActive,
    handleToggleFreeSession,
    handleToggleParentalConsent,
    handleToggleAdultsOnly,
  } = useServiceFormState();
  
  const { validateForm } = useServiceValidation();
  const { toast } = useToast();
  const [editingService, setEditingService] = useState<Service | null>(null);

  const handleEdit = (service: Service) => {
    setEditingService(service);
    setNewService({
      service_name: service.service_name,
      description: service.description || '',
      session_instructions: service.session_instructions || '',
      price: service.price.toString(),
      duration_minutes: service.duration_minutes,
      formats_available: service.formats_available,
      currency: service.currency,
      is_active: service.is_active,
      is_free_session: service.is_free_session,
      parental_consent_required: service.parental_consent_required || false,
      adults_only: service.adults_only || false,
    });
    
    // Set selected formats
    const formatState: Record<string, boolean> = {
      in_person: false,
      virtual: false,
    };
    service.formats_available.forEach(format => {
      formatState[format] = true;
    });
    setSelectedFormats(formatState);
  };

  const handleCancelEdit = () => {
    setEditingService(null);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm(newService, selectedFormats)) {
      return;
    }

    const formats = Object.entries(selectedFormats)
      .filter(([_, selected]) => selected)
      .map(([format]) => format);

    const serviceData = {
      ...newService,
      price: Number(newService.price),
      formats_available: formats,
    };

    let success = false;
    if (editingService) {
      success = await updateService(editingService.id, serviceData);
      if (success) {
        setEditingService(null);
      }
    } else {
      success = await addService(serviceData);
    }
    
    if (success) {
      resetForm();
    }
  };

  return (
    <div className="space-y-6">
      <ServicesHeader servicesCount={services.length} />

      <ServicesContent
        services={services}
        isLoading={isLoading}
        onToggleActive={toggleServiceActive}
        onEdit={handleEdit}
        onDelete={deleteService}
      />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{editingService ? 'Edit Service' : 'Add New Service'}</CardTitle>
              <CardDescription>
                {editingService ? 'Update your service details' : 'Create a new service to offer to your clients'}
              </CardDescription>
            </div>
            {editingService && (
              <Button variant="outline" onClick={handleCancelEdit}>
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <NewServiceForm
            newService={newService}
            selectedFormats={selectedFormats}
            isSubmitting={isSubmitting}
            onInputChange={handleInputChange}
            onSelectChange={handleSelectChange}
            onFormatChange={handleFormatChange}
            onToggleActive={handleToggleActive}
            onToggleFreeSession={handleToggleFreeSession}
            onToggleParentalConsent={handleToggleParentalConsent}
            onToggleAdultsOnly={handleToggleAdultsOnly}
            onSubmit={handleSubmit}
          />
        </CardContent>
      </Card>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Managing Services</AlertTitle>
        <AlertDescription>
          You can edit, activate/deactivate, or delete your services using the action buttons in the table above.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default ServicesManager;